import React, { Suspense } from "react";
import { Navigate, createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";

import { routePaths } from "@/config/routes";
import { useAuthContext } from "@/context/auth";
import { ConsentView } from "@/views/ConsentView";
import { InvitationView } from "@/views/InvitationView";
import { LoadingView } from "@/views/LoadingView";
import { LoginView } from "@/views/LoginView";
import { MainView } from "@/views/MainView";
import { NoPermissionsView } from "@/views/NoPermissionsView";
import { PasswordResetView } from "@/views/PasswordResetView";
import { PasswordSetView } from "@/views/PasswordSetView";
import { RunAsView } from "@/views/RunAsView";
import { SupportCodeView } from "@/views/SupportCodeView";
import { ThirdPartyConnectionView } from "@/views/ThirdPartyConnectionView";
import { AccountSecurityView } from "@/views/security/AccountSecurityView";
import { TwoFactorSetupWizard } from "@/views/security/TwoFactorSetupWizard";

import { LegacyRedirect } from "./LegacyRedirect";
import { RouteErrorView } from "./RouteErrorView";
import { lazyWithReload } from "./lazyWithReload";

const LazyUserManagerApp = lazyWithReload(() => import("@/features/userManagerApp"));
const LazyUsersView = lazyWithReload(() => import("@/features/users"));
const LazyUserDetailsView = lazyWithReload(() => import("@/features/userDetails"));
const LazyRolesView = lazyWithReload(() => import("@/features/roles"));
const LazyRoleDetailsView = lazyWithReload(() => import("@/features/roleDetails"));
const LazyCreateRoleView = lazyWithReload(() => import("@/features/createRole"));
const LazyAdminSettingsView = lazyWithReload(() => import("@/features/adminSettings"));
const LazyCompanySettingsView = lazyWithReload(() => import("@/features/companySettings"));
const LazyImportsView = lazyWithReload(() => import("@/features/imports"));
const LazySecurityLogsView = lazyWithReload(() => import("@/features/securityLogs"));
const LazyDynamicRolesView = lazyWithReload(() => import("@/features/dynamicRoles"));
const LazyAdGroupsView = lazyWithReload(() => import("@/features/adGroups"));
const LazySkillPanelDashboardsView = lazyWithReload(
  () => import("@/features/skillPanelDashboards"),
);
const LazyProductMappingPanel = lazyWithReload(() =>
  import("@/features/adGroups/ProductMappingPanel").then((m) => ({
    default: m.ProductMappingPanel,
  })),
);
const LazyRoleMappingPanel = lazyWithReload(() =>
  import("@/features/adGroups/RoleMappingPanel").then((m) => ({ default: m.RoleMappingPanel })),
);
const LazyDynamicRoleAssignmentPanel = lazyWithReload(() =>
  import("@/features/dynamicRoles/DynamicRoleAssignmentPanel").then((m) => ({
    default: m.DynamicRoleAssignmentPanel,
  })),
);
const LazyCreateUserView = lazyWithReload(() =>
  import("@/features/users/CreateUserView").then((m) => ({ default: m.CreateUserView })),
);
const LazyAssistantView = lazyWithReload(() => import("@/features/assistant"));
const LazyTeamsView = lazyWithReload(() => import("@/features/teams"));
const LazyTeamDetailsPanel = lazyWithReload(() => import("@/features/teams/TeamDetailsPanel"));
const LazyDashboardView = lazyWithReload(() => import("@/features/dashboard"));
const LazyReportsView = lazyWithReload(() => import("@/features/reports"));
const LazyProvisioningView = lazyWithReload(() => import("@/features/provisioning"));
const LazyAccessRequestsView = lazyWithReload(() => import("@/features/accessRequests"));
const LazyAccessRequestApprovalView = lazyWithReload(() =>
  import("@/features/accessRequests").then((m) => ({ default: m.AccessRequestApprovalView })),
);
const LazyCreateAccessRequestView = lazyWithReload(() =>
  import("@/features/accessRequests").then((m) => ({ default: m.CreateAccessRequestView })),
);

const ProtectedRoute = (props: { children: React.ReactNode }) => {
  const { isLoggedIn } = useAuthContext();
  if (isLoggedIn) {
    return <>{props.children}</>;
  }
  const currentUrl = window.location.origin + window.location.pathname + window.location.search;
  return (
    <Navigate to={`${routePaths.LOGIN}?redirect_url=${encodeURIComponent(currentUrl)}`} replace />
  );
};

const PermissionProtectedRoute = (props: {
  children: React.ReactNode;
  permission: string | string[];
}) => {
  const { user, fetchingPermissions } = useAuthContext();
  const required = Array.isArray(props.permission) ? props.permission : [props.permission];
  const hasAccess = required.some((permission) => user?.authorities?.includes(permission) ?? false);

  if (fetchingPermissions) {
    return <LoadingView />;
  }
  return hasAccess ? <>{props.children}</> : <NoPermissionsView />;
};

const createAppRouter = () =>
  createBrowserRouter([
    {
      // Catches a render-time throw in any route the admin app's boundary below does not cover
      errorElement: <RouteErrorView />,
      children: [
        { path: routePaths.LOGIN, element: <LoginView /> },
        { path: routePaths.RESET_PASSWORD, element: <PasswordResetView /> },
        { path: routePaths.SET_PASSWORD, element: <PasswordSetView /> },
        { path: routePaths.INVITATION, element: <InvitationView /> },
        {
          path: routePaths.PARTNERS,
          children: [{ path: "linkedin/onboarding", element: <ThirdPartyConnectionView /> }],
        },
        {
          path: routePaths.SUPPORT_CODE,
          element: (
            <ProtectedRoute>
              <SupportCodeView />
            </ProtectedRoute>
          ),
        },
        {
          path: routePaths.ACCESS_REQUEST_APPROVAL,
          element: (
            <ProtectedRoute>
              <Suspense fallback={<LoadingView />}>
                <LazyAccessRequestApprovalView />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: routePaths.ACCESS_REQUEST_DENY,
          element: (
            <ProtectedRoute>
              <Suspense fallback={<LoadingView />}>
                <LazyAccessRequestApprovalView />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        { path: routePaths.RUN_AS, element: <RunAsView /> },
        {
          path: routePaths.OAUTH2_AUTHORIZE,
          element: (
            <PermissionProtectedRoute permission="auth:oauth:grant">
              <ConsentView />
            </PermissionProtectedRoute>
          ),
        },
        {
          path: routePaths.PRODUCTS_VIEW,
          element: (
            <ProtectedRoute>
              <MainView />
            </ProtectedRoute>
          ),
        },
        {
          path: routePaths.SECURITY,
          element: (
            <ProtectedRoute>
              <AccountSecurityView />
            </ProtectedRoute>
          ),
        },
        {
          path: routePaths.SECURITY_TWO_FACTOR_SETUP,
          element: (
            <ProtectedRoute>
              <TwoFactorSetupWizard />
            </ProtectedRoute>
          ),
        },
        {
          path: routePaths.USER_MANAGER_APP.ROOT,
          element: (
            <ProtectedRoute>
              <PermissionProtectedRoute permission="auth:user">
                <Suspense fallback={<LoadingView />}>
                  <LazyUserManagerApp />
                </Suspense>
              </PermissionProtectedRoute>
            </ProtectedRoute>
          ),
          children: [
            {
              // Pathless, so a failing view is replaced inside the app shell and the sidebar still works
              errorElement: <RouteErrorView insideApp />,
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<LoadingView />}>
                      <LazyDashboardView />
                    </Suspense>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.USERS_VIEW_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:user:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazyUsersView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                  children: [
                    {
                      path: "new",
                      element: (
                        <PermissionProtectedRoute permission="auth:user:create">
                          <Suspense fallback={<LoadingView />}>
                            <LazyCreateUserView />
                          </Suspense>
                        </PermissionProtectedRoute>
                      ),
                    },
                    {
                      path: ":id",
                      element: (
                        <Suspense fallback={<LoadingView />}>
                          <LazyUserDetailsView />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: routePaths.USER_MANAGER_APP.ROLES_VIEW_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:role:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazyRolesView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                  children: [
                    {
                      path: "create",
                      element: (
                        <Suspense fallback={<LoadingView />}>
                          <LazyCreateRoleView />
                        </Suspense>
                      ),
                    },
                    {
                      path: ":id",
                      element: (
                        <Suspense fallback={<LoadingView />}>
                          <LazyRoleDetailsView />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: routePaths.USER_MANAGER_APP.ADMIN_PANEL_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:superadmin">
                      <Suspense fallback={<LoadingView />}>
                        <LazyAdminSettingsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.COMPANY_SETTINGS_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:company:edit">
                      <Suspense fallback={<LoadingView />}>
                        <LazyCompanySettingsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.IMPORTS_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:userimports:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazyImportsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.SECURITY_LOGS_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:securitylogs:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazySecurityLogsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.DYNAMIC_ROLES_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:role:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazyDynamicRolesView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                  children: [
                    {
                      path: "new",
                      element: (
                        <PermissionProtectedRoute permission="auth:superadmin">
                          <Suspense fallback={<LoadingView />}>
                            <LazyDynamicRoleAssignmentPanel />
                          </Suspense>
                        </PermissionProtectedRoute>
                      ),
                    },
                    {
                      path: ":id",
                      element: (
                        <Suspense fallback={<LoadingView />}>
                          <LazyDynamicRoleAssignmentPanel />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: `${routePaths.USER_MANAGER_APP.LEGACY_DYNAMIC_ROLES_RELATIVE}/*`,
                  element: (
                    <LegacyRedirect
                      from={routePaths.USER_MANAGER_APP.LEGACY_DYNAMIC_ROLES_ABSOLUTE}
                      to={routePaths.USER_MANAGER_APP.DYNAMIC_ROLES_ABSOLUTE}
                    />
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.AD_GROUPS_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:role:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazyAdGroupsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                  children: [
                    {
                      path: "products/new",
                      element: (
                        <PermissionProtectedRoute permission="auth:superadmin">
                          <Suspense fallback={<LoadingView />}>
                            <LazyProductMappingPanel />
                          </Suspense>
                        </PermissionProtectedRoute>
                      ),
                    },
                    {
                      path: "products/:id",
                      element: (
                        <PermissionProtectedRoute permission="auth:superadmin">
                          <Suspense fallback={<LoadingView />}>
                            <LazyProductMappingPanel />
                          </Suspense>
                        </PermissionProtectedRoute>
                      ),
                    },
                    {
                      path: "roles/new",
                      element: (
                        <PermissionProtectedRoute permission="auth:superadmin">
                          <Suspense fallback={<LoadingView />}>
                            <LazyRoleMappingPanel />
                          </Suspense>
                        </PermissionProtectedRoute>
                      ),
                    },
                    {
                      path: "roles/:id",
                      element: (
                        <PermissionProtectedRoute permission="auth:superadmin">
                          <Suspense fallback={<LoadingView />}>
                            <LazyRoleMappingPanel />
                          </Suspense>
                        </PermissionProtectedRoute>
                      ),
                    },
                  ],
                },
                {
                  path: `${routePaths.USER_MANAGER_APP.LEGACY_AD_GROUPS_RELATIVE}/*`,
                  element: (
                    <LegacyRedirect
                      from={routePaths.USER_MANAGER_APP.LEGACY_AD_GROUPS_ABSOLUTE}
                      to={routePaths.USER_MANAGER_APP.AD_GROUPS_ABSOLUTE}
                    />
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.SKILLPANEL_DASHBOARDS_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:role:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazySkillPanelDashboardsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.ASSISTANT_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:ai:chat">
                      <Suspense fallback={<LoadingView />}>
                        <LazyAssistantView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.TEAMS_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:team:read">
                      <Suspense fallback={<LoadingView />}>
                        <LazyTeamsView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                  children: [
                    {
                      path: ":teamId",
                      element: (
                        <Suspense fallback={<LoadingView />}>
                          <LazyTeamDetailsPanel />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: routePaths.USER_MANAGER_APP.REPORTS_RELATIVE,
                  element: (
                    <Suspense fallback={<LoadingView />}>
                      <LazyReportsView />
                    </Suspense>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.PROVISIONING_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:provisioning:manage">
                      <Suspense fallback={<LoadingView />}>
                        <LazyProvisioningView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.CREATE_ACCESS_REQUEST_RELATIVE,
                  element: (
                    <PermissionProtectedRoute permission="auth:accessrequest:request">
                      <Suspense fallback={<LoadingView />}>
                        <LazyCreateAccessRequestView />
                      </Suspense>
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: routePaths.USER_MANAGER_APP.ACCESS_REQUESTS_RELATIVE,
                  element: (
                    <Suspense fallback={<LoadingView />}>
                      <LazyAccessRequestsView />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
        { path: "*", element: <Navigate to={routePaths.PRODUCTS_VIEW} replace /> },
      ],
    },
  ]);

const AppRouting = () => <RouterProvider router={createAppRouter()} />;

export default AppRouting;
